import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import _ from 'lodash'
import { toast } from 'react-toastify'

import Annotations from '../Annotations'
import VideoPlayer from 'components/VideoPlayer'
import { learningApi } from 'services/api'

import { BsCheck } from 'react-icons/bs'
import { FiCheckCircle, FiCircle } from 'react-icons/fi'

import {
  Container,
  VideosList,
  ContainerFrame,
  ContainerTitle,
  Wrapper,
  ImageVideoList,
  Video,
  NameVideo,
} from './styles'
import { useLessonContext } from 'pages/Lesson/context'
import { VideoJsPlayer } from 'video.js'
import { useLessonParams } from 'pages/Lesson/hooks/use-lesson-params'
import { IContentVideo } from 'pages/Lesson/hooks/use-get-lesson/types'
import { useLocation } from 'react-router'
import { useMount } from 'react-use'
import { PromiseQueue } from 'services/promise-queue'

const promiseQueue = new PromiseQueue({ delay: 500 })

const INTERVAL_TO_SEND_MONITORINGS = 30

interface ContentLessonProps {
  setObject(any): void
}

const VideoLesson: React.FC<ContentLessonProps> = ({ setObject }) => {
  const location = useLocation()
  const token = localStorage.getItem('token')
  const [currentTime, setCurrentTime] = useState<number>(0)
  const playerRef = useRef<VideoJsPlayer | null>(null)
  const [openVideoList] = useState<boolean>(true)

  const [selectedVideoId, setSelectedVideoId] = useState('')

  const { chapterId, lessonId, courseId, moduleId } = useLessonParams()
  const { swrMenuContent, swrLesson } = useLessonContext()

  const menuData = swrMenuContent.data
  const { data: lessonData, videoContents } = swrLesson

  const selectedVideo: IContentVideo = useMemo(() => {
    if (!selectedVideoId) return null

    const index = videoContents.findIndex((video) => video._id === selectedVideoId)

    if (index === -1) return null

    const video = videoContents[index]

    return { ...video, order: index }
  }, [videoContents, selectedVideoId])

  useEffect(() => {
    setObject(selectedVideo)
  }, [setObject, selectedVideo])

  const getVideoByParam = () => {
    const queryString = new URLSearchParams(location.search || '')

    const videoId = queryString.get('video')
    if (!videoId) return

    return videoContents.find((video) => video._id === videoId)
  }

  useMount(() => {
    const videoByParam = getVideoByParam()

    if (videoByParam) {
      setSelectedVideoId(videoByParam._id)

      return
    }

    if (!videoContents.length) return

    let currentVideo = videoContents[0]

    if (videoContents.some((v) => v.updatedAt)) {
      currentVideo = _.last(
        _.sortBy(
          videoContents.filter((v) => v.updatedAt),
          ['updatedAt']
        )
      )
    }

    setSelectedVideoId(currentVideo._id)
  })

  const videoJsOptions = {
    autoplay: false,
    preload: 'auto',
    controls: true,
    responsive: true,
    fluid: true,
    controlBar: {
      pictureInPictureToggle: false,
    },
    userActions: { hotkeys: true },
    playbackRates: [0.5, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 3],
    plugins: {
      seekButtons: {
        forward: 10,
        back: 10,
      },
    },
    poster: selectedVideo?.media?.thumbnail,
    sources: [
      {
        src: selectedVideo?.media?.url,
        _id: selectedVideo?._id,
      },
    ],
    html5: {
      nativeAudioTracks: true,
      nativeVideoTracks: true,
      nativeTextTracks: true,
    },
  }

  const handlePlayerReady = (player: VideoJsPlayer) => {
    playerRef.current = player

    player.on('dispose', () => {
      console.log('player will dispose')
    })

    player.on('seeked', () => {
      const time = Math.floor(player.currentTime())

      setCurrentTime(time || 0)
    })

    player.one('loadeddata', () => {
      setCurrentTime(selectedVideo.current || 0)
      player.currentTime(selectedVideo.current || 0)
    })
  }

  const changePlayerOptions = async (video: IContentVideo) => {
    if (!playerRef.current) return
    if (video._id === selectedVideoId) return

    setSelectedVideoId(video._id)

    playerRef.current.pause()
    playerRef.current.poster(video.media.thumbnail)

    const src = video.media.url.replace(' ', '')
    playerRef.current.src([{ src }])

    setCurrentTime(video.current || 0)
    playerRef.current.currentTime(video.current || 0)
  }

  const getSourceType = useCallback(() => {
    if (menuData.course?.isFreemium) {
      return 'freemium'
    }

    if (menuData.course?.isSubscription) {
      return 'subscription'
    }

    return 'course'
  }, [menuData.course])

  const sendMonitoring = async (userAction = 0) => {
    const postMonitoring = async () => {
      try {
        await learningApi.post(
          '/content/monitoring/video',
          {
            course: { _id: courseId, name: menuData.course?.name },
            module: { _id: moduleId, name: lessonData.module?.name },
            lesson: { _id: lessonId, name: lessonData?.name },
            chapter: { _id: chapterId, name: lessonData.chapter?.name },
            object: {
              _id: selectedVideo?._id,
              name: selectedVideo?.name,
            },
            current: currentTime,
            duration: selectedVideo?.media?.total,
            userAction,
            source: getSourceType(),
            subscription: menuData.course?.subscription,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      } catch (err) {
        if (err.response.status === 401) return
        console.error(err)
        toast.error('Houve um erro ao registrar o seu progresso!')
      }
    }

    promiseQueue.add(postMonitoring)
  }

  const mutateVideo = async (properties: Partial<IContentVideo>) => {
    const mutatedData = await swrLesson.mutateVideo({
      videoId: selectedVideo._id,
      properties,
    })

    const toggleCompleted = selectedVideo.completed !== properties.completed
    const isCompletedByExcerto =
      swrLesson.excertoContents.length > 0
        ? swrLesson.excertoContents.every((excerto) => excerto.completed)
        : false

    const shouldMutateMenu = toggleCompleted && isCompletedByExcerto === false

    if (shouldMutateMenu) {
      const { contents } = mutatedData

      const isChapterCompleted = contents
        .filter((content) => content.type === 'video')
        .every((video) => video.completed)

      swrMenuContent.completeChapter({ lessonId, chapterId, isCompleted: isChapterCompleted })
    }
  }

  const onTimeUpdate = (time: number) => {
    if (!selectedVideo) return
    if (!time) return
    if (selectedVideo?.media?.total === null) return

    const isPaused = playerRef.current.paused()

    if (isPaused) return

    const isInInterval = time % INTERVAL_TO_SEND_MONITORINGS === 0
    const isEnded = time === selectedVideo.media.total
    const isCompleted = (time / selectedVideo?.media?.total) * 100 > 95

    if (isInInterval || isEnded || isCompleted) {
      sendMonitoring()
      mutateVideo({
        completed: isCompleted,
        current: time,
      })
    }

    setCurrentTime(time || 0)
  }

  const handleVideoComplete = (completed) => {
    const userAction = completed ? 1 : -1
    sendMonitoring(userAction)

    mutateVideo({ completed })
  }

  return (
    <Container>
      <Wrapper>
        <ContainerFrame>
          {selectedVideo?.media && (
            <VideoPlayer
              options={videoJsOptions}
              onReady={handlePlayerReady}
              setCurrentTime={onTimeUpdate}
            />
          )}
        </ContainerFrame>
        <ContainerTitle>
          <h3>
            {selectedVideo?.order < 9 ? `0${selectedVideo?.order + 1}` : selectedVideo?.order + 1} -{' '}
            {selectedVideo?.name}
          </h3>
          <div>
            {selectedVideo?.completed ? (
              <>
                <div title="Desmarcar vídeo como assistido">
                  <FiCheckCircle
                    color="#14CBBC"
                    onClick={() => handleVideoComplete(false)}
                    size={24}
                  />
                </div>
                {`Concluído`}
              </>
            ) : (
              <>
                <div title="Marcar vídeo como assistido">
                  <FiCircle color="#FFF" onClick={() => handleVideoComplete(true)} size={24} />
                </div>
                {`Pendente`}
              </>
            )}
          </div>
        </ContainerTitle>
        <Annotations
          slide={selectedVideo?.media?.slide}
          currentTime={currentTime}
          courseId={courseId}
          courseName={menuData.course?.name}
          chapterId={chapterId}
          lessonId={lessonId}
          moduleId={moduleId}
          moduleName={lessonData.module?.name}
          objectId={selectedVideo?._id}
          videoId={selectedVideo?._id}
          isText={false}
          teacherId={selectedVideo?.teacher?._id}
          teacherName={selectedVideo?.teacher?.name}
          courseInfos={menuData.course}
          moduleInfos={lessonData.module}
        />
      </Wrapper>
      <VideosList showList={openVideoList}>
        <div className="nextVideo">Próximos vídeos:</div>
        <div>
          {videoContents.map((video, idx) => {
            const durationParts = video.media?.duration.split(':')
            const hours = parseInt(durationParts[0])
            const minutes = parseInt(durationParts[1])
            const seconds = parseInt(durationParts[2])

            const formatSeconds = (sec) => sec.toString().padStart(2, '0')

            return (
              <Video
                onClick={() => {
                  changePlayerOptions(video)
                }}
                key={idx}
              >
                <ImageVideoList>
                  <div className={`video ${selectedVideoId === video._id ? 'selectedVideo' : ''}`}>
                    {video.completed ? (
                      <div className="watchedText">
                        <BsCheck size={20} /> Visto
                      </div>
                    ) : null}
                    <img src={video.media?.thumbnail} alt="thumb" height="100" width="170" />
                    <span className="video-duration">
                      {hours > 0
                        ? `${hours}:${minutes}:${formatSeconds(seconds)}`
                        : `${minutes}:${formatSeconds(seconds)}`}
                    </span>
                  </div>
                </ImageVideoList>
                <NameVideo>
                  <div className={`video ${selectedVideoId === video._id ? 'selectedText' : ''}`}>
                    {idx + 1 < 10 ? `0${idx + 1}` : idx + 1} - {video.name}
                  </div>
                </NameVideo>
              </Video>
            )
          })}
        </div>
      </VideosList>
    </Container>
  )
}

export default VideoLesson
